import React, { useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { connect } from "react-redux";
import * as actions from "redux/actions/sessionActions";
import logo1 from 'assets/images/ebiTrade_white.png';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';
import 'assets/css/signin.css';
import { FaEyeSlash } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import * as actions_event from "redux/actions/eventActions";
import { Translate, I18n } from "react-redux-i18n";
import { translateApiError } from 'utils/passwordPolicy';

const REMEMBER_EMAIL_KEY = 'ebi_remember_login_email';

const passwordSchema = {
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
    length: { maximum: 64 },
  },
  password: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { maximum: 128 },
  },
};

const emailSchema = {
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
    length: { maximum: 64 },
  },
};

const SignIn = (props) => {
  const { history } = props;

  const savedEmail = (() => {
    try {
      return localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
    } catch (e) {
      return '';
    }
  })();

  const [loginMode, setLoginMode] = useState('password'); // password | code
  const [rememberEmail, setRememberEmail] = useState(Boolean(savedEmail));
  const [formState, setFormState] = useState({
    isValid: false,
    values: { password: '', email: savedEmail, code: '' },
    touched: {},
    errors: {},
    showPassword: false,
  });
  const [twoFactor, setTwoFactor] = useState(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    props.Logout();
    props.closeConextionSocket();
  }, []);

  useEffect(() => {
    const schema = loginMode === 'password' && !twoFactor
      ? passwordSchema
      : emailSchema;
    const errors = validate(
      { email: formState.values.email, password: formState.values.password },
      schema,
    );
    setFormState((prev) => ({
      ...prev,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values.email, formState.values.password, loginMode, twoFactor]);

  useEffect(() => {
    if (!props.error) {
      return;
    }
    const parsed = translateApiError(props.error);
    const text = parsed.count != null
      ? I18n.t(parsed.key, { count: parsed.count })
      : I18n.t(parsed.key);
    alertify.error(text !== parsed.key ? text : props.error);
  }, [props.error]);

  const handleChange = (event) => {
    event.persist();
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value,
      },
      touched: {
        ...prev.touched,
        [event.target.name]: true,
      },
    }));
  };

  const persistRememberedEmail = (email, remember) => {
    try {
      const value = String(email || '').trim();
      if (remember && value) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, value);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch (e) {
      // ignore storage errors
    }
  };

  const handleRememberChange = (event) => {
    const checked = event.target.checked;
    setRememberEmail(checked);
    persistRememberedEmail(formState.values.email, checked);
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setCodeSent(false);
    setTwoFactor(null);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, password: '', code: '' },
    }));
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    persistRememberedEmail(formState.values.email, rememberEmail);
    const result = await props.Login(formState.values.email, formState.values.password);
    if (result?.requires2FA) {
      setTwoFactor({
        tempToken: result.tempToken,
        method: result.method,
        code: '',
      });
      return;
    }
    if (result?.success) {
      history.push('/');
    }
  };

  const handleVerify2fa = async (event) => {
    event.preventDefault();
    const result = await props.Verify2fa({
      tempToken: twoFactor.tempToken,
      code: twoFactor.code,
    });
    if (result?.success) {
      history.push('/');
    }
  };

  const handleSendLoginCode = async (event) => {
    event.preventDefault();
    if (!formState.values.email || sendingCode) {
      return;
    }
    persistRememberedEmail(formState.values.email, rememberEmail);
    setSendingCode(true);
    try {
      const result = await props.SendLoginCode(formState.values.email);
      if (result?.success) {
        setCodeSent(true);
        alertify.success(I18n.t('login_code_sent'));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyLoginCode = async (event) => {
    event.preventDefault();
    if (!formState.values.email || !formState.values.code) {
      return;
    }
    persistRememberedEmail(formState.values.email, rememberEmail);
    const result = await props.VerifyLoginCode({
      email: formState.values.email,
      code: formState.values.code,
    });
    if (result?.success) {
      history.push('/');
    }
  };

  const handleClickShowPassword = () => {
    setFormState((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  return (
    <div className='sign-in-page'>
      <div className='signin-container'>
        <a
          className='signin-hero'
          href="https://www.ebitrade.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="EbiTrade"
        >
          <img alt="Logo" src={logo1} className='img-logo' />
          <h1>
            Trading Log / Bitácora de Trading
          </h1>
          <p>
            Elecsotech
          </p>
        </a>
        <div className='signin-card'>
          <div className='sign-in-content'>
            <div className='signin-header'>
              <h2 className='signin-title'>
                <Translate value="sign_in" />
              </h2>
              <span className="signin-subtitle">
                <Translate value="login_email" />
              </span>
            </div>

            {!twoFactor && (
              <div className="signin-mode-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  className={loginMode === 'password' ? 'signin-mode-tab is-active' : 'signin-mode-tab'}
                  aria-selected={loginMode === 'password'}
                  onClick={() => switchMode('password')}
                >
                  <Translate value="login_with_password" />
                </button>
                <button
                  type="button"
                  role="tab"
                  className={loginMode === 'code' ? 'signin-mode-tab is-active' : 'signin-mode-tab'}
                  aria-selected={loginMode === 'code'}
                  onClick={() => switchMode('code')}
                >
                  <Translate value="login_with_code" />
                </button>
              </div>
            )}

            {!twoFactor && loginMode === 'password' && (
              <form className='signin-form' onSubmit={handleSignIn}>
                <div className='form-group'>
                  <input
                    className='input'
                    name="email"
                    type="email"
                    required
                    value={formState.values.email || ''}
                    onChange={handleChange}
                    placeholder='Email'
                    autoComplete="email"
                  />
                </div>
                <div className='password-row mt-2'>
                  <input
                    className='input'
                    name="password"
                    type={formState.showPassword ? 'text' : 'password'}
                    onChange={handleChange}
                    placeholder={I18n.t("password")}
                    autoComplete="current-password"
                  />
                  <span className="pwd-toggle" onClick={handleClickShowPassword}>
                    {formState.showPassword ? <FaEye /> : <FaEyeSlash />}
                  </span>
                </div>

                <label className="signin-remember-row" htmlFor="remember-email">
                  <input
                    id="remember-email"
                    className="input-checkbox"
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={handleRememberChange}
                  />
                  <span>
                    <Translate value="remember_user" />
                  </span>
                </label>

                <button
                  type="submit"
                  className="btn btn-primary btn-block mt-2"
                  disabled={!formState.isValid}
                >
                  <Translate value="sign_now" />
                </button>
              </form>
            )}

            {!twoFactor && loginMode === 'code' && (
              <form
                className='signin-form'
                onSubmit={codeSent ? handleVerifyLoginCode : handleSendLoginCode}
              >
                <p className='signin-subtitle'>
                  <Translate value={codeSent ? 'login_code_sent' : 'login_code_hint'} />
                </p>
                {!codeSent && (
                  <>
                    <div className='form-group mt-2'>
                      <input
                        className='input'
                        name="email"
                        type="email"
                        required
                        value={formState.values.email || ''}
                        onChange={handleChange}
                        placeholder='Email'
                        autoComplete="email"
                      />
                    </div>
                    <label className="signin-remember-row" htmlFor="remember-email-code">
                      <input
                        id="remember-email-code"
                        className="input-checkbox"
                        type="checkbox"
                        checked={rememberEmail}
                        onChange={handleRememberChange}
                      />
                      <span>
                        <Translate value="remember_user" />
                      </span>
                    </label>
                    <button
                      type="submit"
                      className="btn btn-primary btn-block mt-2"
                      disabled={!formState.values.email || sendingCode}
                    >
                      {sendingCode
                        ? <Translate value="sending" />
                        : <Translate value="login_send_code" />}
                    </button>
                  </>
                )}
                {codeSent && (
                  <>
                    <div className='form-group mt-2'>
                      <input
                        className='input'
                        name="code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        autoFocus
                        value={formState.values.code || ''}
                        onChange={handleChange}
                        placeholder={I18n.t('login_code_placeholder')}
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-block mt-2"
                      disabled={!formState.values.code}
                    >
                      <Translate value="login_verify_code" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-block mt-2"
                      onClick={() => {
                        setCodeSent(false);
                        setFormState((prev) => ({
                          ...prev,
                          values: { ...prev.values, code: '' },
                        }));
                      }}
                    >
                      <Translate value="back_to_login" />
                    </button>
                  </>
                )}
              </form>
            )}

            {twoFactor && (
              <form className='signin-form' onSubmit={handleVerify2fa}>
                <p className='signin-subtitle'>
                  {twoFactor.method === 'email'
                    ? <Translate value="login_2fa_email_hint" />
                    : <Translate value="login_2fa_totp_hint" />}
                </p>
                <div className='form-group mt-2'>
                  <input
                    className='input'
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={twoFactor.code}
                    onChange={(e) => setTwoFactor({ ...twoFactor, code: e.target.value })}
                    placeholder={I18n.t('login_code_placeholder')}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-block mt-2"
                  disabled={!twoFactor.code}
                >
                  <Translate value="verify" />
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-block mt-2"
                  onClick={() => setTwoFactor(null)}
                >
                  <Translate value="back_to_login" />
                </button>
              </form>
            )}

            {!twoFactor && (
              <p className='signin-sugestion mt-3'>
                <span className='text-muted f-14'>
                  <Translate value="don_have_account" />{' '}
                </span>
                <Link className='linked-primary' to="/signup">
                  <Translate value="sign_up" />
                </Link>
                <span className='text-muted f-14'> {' '} <Translate value="or" /> {' '}</span>
                <Link className='linked-primary' to="/recovery">
                  <Translate value="recovery_password" />
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

SignIn.propTypes = {
  history: PropTypes.object,
};

function mapStateToProps(reducer) {
  return {
    error: reducer.session.error,
    success: reducer.session.success,
  };
}

const mapDispatchToProps = (dispatch) => ({
  Login: (email, password) =>
    dispatch(actions.login({ email, password })),
  Verify2fa: (payload) =>
    dispatch(actions.verify2fa(payload)),
  SendLoginCode: (email) =>
    dispatch(actions.sendLoginCode(email)),
  VerifyLoginCode: (payload) =>
    dispatch(actions.verifyLoginCode(payload)),
  Logout: () =>
    dispatch(actions.logout()),
  closeConextionSocket: () =>
    dispatch(actions_event.closeConextionSocket({})),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
